import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const CartContext = createContext(null);
const DISCOUNT_STORAGE_KEY = "marq_cart_discounts";
const PENDING_PROMO_KEY = "marq_pending_promo";
const PENDING_ADD_KEY = "marq_pending_cart_add";
const TM_REF_COOKIE = "tm_ref";
const TM_REF_LS_KEY = "tm_ref"; // localStorage fallback for cookie wipes
const TM_REF_TTL_DAYS = 90;

function loadStoredDiscounts() {
  try {
    const raw = localStorage.getItem(DISCOUNT_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function readCookie(name) {
  if (typeof document === "undefined") return null;
  const prefix = name + "=";
  for (const c of document.cookie.split(";")) {
    const s = c.trim();
    if (s.startsWith(prefix)) return decodeURIComponent(s.slice(prefix.length));
  }
  return null;
}

function writeTmRefCookie(ref) {
  try {
    const isHttps = window.location.protocol === "https:";
    // SameSite=None+Secure survives cross-site redirects (Facebook in-app browser,
    // Google OAuth round-trips, iMessage previews). Falls back to Lax on http (dev only).
    const attrs = isHttps
      ? `samesite=none; secure`
      : `samesite=lax`;
    document.cookie = `${TM_REF_COOKIE}=${encodeURIComponent(ref)}; path=/; max-age=${TM_REF_TTL_DAYS * 24 * 3600}; ${attrs}`;
  } catch { /* ignore */ }
}

function readTmRef() {
  // Cookie first (per Trackmatrix spec), then localStorage fallback (TTL-checked).
  const fromCookie = readCookie(TM_REF_COOKIE);
  if (fromCookie) return fromCookie;
  try {
    const raw = localStorage.getItem(TM_REF_LS_KEY);
    if (!raw) return null;
    const { ref, captured_at } = JSON.parse(raw);
    if (!ref) return null;
    const ageDays = (Date.now() - new Date(captured_at).getTime()) / 86_400_000;
    if (ageDays > TM_REF_TTL_DAYS) {
      localStorage.removeItem(TM_REF_LS_KEY);
      return null;
    }
    return ref;
  } catch {
    return null;
  }
}

function clearTmRef() {
  try {
    document.cookie = `${TM_REF_COOKIE}=; path=/; max-age=0; samesite=lax`;
    localStorage.removeItem(TM_REF_LS_KEY);
  } catch { /* ignore */ }
}

// Capture ?promo= and ?ref= on demand. Runs once on module load AND on every
// Try to extract an affiliate slug from `document.referrer`. Useful when an
// upstream redirector (e.g. trackmatrix.net/api/p/SLUG?p=PRODUCT_ID,
// trackmatrix.net/r/SLUG) strips the slug from the destination URL before
// redirecting the customer to our site. We pull it back from the Referer
// header instead. Returns the slug or null.
//
// Matches:
//   trackmatrix.net/api/p/mr-5dc2fc?p=prod_xxx
//   trackmatrix.net/r/mr-5dc2fc?to=/products/...
//   trackmatrix.net/p/mr-5dc2fc
//   l.facebook.com/l.php?u=<encoded-trackmatrix-url>    (FB share wrapper)
//   lm.facebook.com/l.php?u=...                          (FB mobile)
//   l.instagram.com/?u=<encoded-trackmatrix-url>         (IG share wrapper)
const _TRUSTED_REF_HOSTS = /(^|\.)trackmatrix\.net$/i;
const _WRAPPER_HOSTS = /(^|\.)(?:l|lm)\.facebook\.com$|(^|\.)l\.instagram\.com$/i;

function _slugFromTrackmatrixUrl(u) {
  try {
    if (!_TRUSTED_REF_HOSTS.test(u.hostname)) return null;
    const m = u.pathname.match(/\/(?:api\/)?(?:r|p|share)\/([A-Za-z0-9][A-Za-z0-9_-]{2,80})/);
    if (m && m[1]) return m[1];
    const q = u.searchParams.get("ref");
    if (q && q.length >= 3 && q.length <= 80) return q;
    return null;
  } catch {
    return null;
  }
}

function _extractRefFromReferrer() {
  if (typeof document === "undefined" || !document.referrer) return null;
  try {
    const u = new URL(document.referrer);
    // Direct trackmatrix.net referrer (the common case)
    const direct = _slugFromTrackmatrixUrl(u);
    if (direct) return direct;
    // Social-network share wrapper: peel off `?u=<encoded-trackmatrix-url>`
    if (_WRAPPER_HOSTS.test(u.hostname)) {
      const wrapped = u.searchParams.get("u");
      if (wrapped) {
        try {
          const inner = new URL(wrapped);
          return _slugFromTrackmatrixUrl(inner);
        } catch { /* not a URL — bail */ }
      }
    }
    return null;
  } catch {
    return null;
  }
}

// Pulls `ref` and `promo` from URL params, drops them from the visible URL,
// stores the ref in (a) JS cookie, (b) localStorage, (c) server (via
// /api/session/affiliate which also sets the HttpOnly cookie). Runs on
// initial page load AND on every in-app route change (CartProvider effect
// below) — critical for users who were already on the site when they tapped
// an affiliate link.
function _captureLandingParams() {
  if (typeof window === "undefined") return;
  try {
    const url = new URL(window.location.href);
    let touched = false;
    const promo = url.searchParams.get("promo");
    if (promo) {
      localStorage.setItem(PENDING_PROMO_KEY, promo.trim().toUpperCase());
      url.searchParams.delete("promo");
      touched = true;
    }
    // Prefer explicit ?ref= over Referer parsing — the URL is authoritative.
    let ref = url.searchParams.get("ref");
    let refFromReferrer = false;
    if (!ref) {
      const fromReferrer = _extractRefFromReferrer();
      if (fromReferrer) {
        ref = fromReferrer;
        refFromReferrer = true;
      }
    }
    if (ref) {
      const trimmed = ref.trim();
      writeTmRefCookie(trimmed);
      try {
        localStorage.setItem(
          TM_REF_LS_KEY,
          JSON.stringify({ ref: trimmed, captured_at: new Date().toISOString(), via: refFromReferrer ? "referrer" : "query" }),
        );
      } catch { /* ignore */ }
      const product_id = url.searchParams.get("p") || null;
      // Fire-and-forget: notify listeners (AffiliateBanner) BOTH before and
      // after the server-side POST so the banner refreshes even if the POST
      // is still in-flight. The pre-POST dispatch fixes the race where the
      // banner mounted and read the STALE cookie before this ran.
      try { window.dispatchEvent(new Event("affiliate-ref-updated")); } catch { /* ignore */ }
      api.post("/session/affiliate", { ref: trimmed, product_id })
        .then(() => { try { window.dispatchEvent(new Event("affiliate-ref-updated")); } catch { /* ignore */ } })
        .catch(() => {});
      if (!refFromReferrer) {
        url.searchParams.delete("ref");
        url.searchParams.delete("p");
        touched = true;
      }
    }
    if (touched) {
      const cleaned = url.pathname + (url.searchParams.toString() ? "?" + url.searchParams.toString() : "") + url.hash;
      window.history.replaceState({}, "", cleaned);
    }
  } catch { /* ignore */ }
}

// First call on module load (cold start of the SPA)
_captureLandingParams();

export function CartProvider({ children }) {
  const { user, loading: authLoading, loginWithGoogle } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [cartLoaded, setCartLoaded] = useState(false);

  // Re-capture ?ref= / ?promo= on every route change. Critical for users who
  // were already on the site when they tapped an affiliate link — in that case
  // React Router navigates in-app without a full reload, so the module-level
  // IIFE wouldn't have re-run.
  useEffect(() => {
    _captureLandingParams();
  }, [location.pathname, location.search]);
  // discounts: array of { code, seller_id, seller_name, discount_amount, ... }
  const [discounts, setDiscounts] = useState(loadStoredDiscounts);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  // Persist applied discounts so they survive refresh / direct navigation to /checkout.
  useEffect(() => {
    try {
      if (discounts.length === 0) localStorage.removeItem(DISCOUNT_STORAGE_KEY);
      else localStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify(discounts));
    } catch { /* ignore quota errors */ }
  }, [discounts]);

  const refresh = useCallback(async () => {
    // Wait until auth has resolved so we don't clobber rehydrated discounts on a
    // page refresh where user is briefly null while /auth/me is in flight.
    if (authLoading) return;
    if (!user) { setItems([]); setDiscounts([]); setCartLoaded(true); return; }
    try {
      const { data } = await api.get("/cart");
      setItems(data);
    } catch {
      setItems([]);
    } finally {
      setCartLoaded(true);
    }
  }, [user, authLoading]);

  useEffect(() => { refresh(); }, [refresh]);

  // When the user becomes authenticated, re-attach the locally-stored tm_ref
  // to their server-side session so checkout can resolve it even if Chrome's
  // OAuth round-trip dropped the cookie. Runs once per (user, ref) pair via a ref-tracking sentinel.
  const attachedRef = useRef(null);
  useEffect(() => {
    if (authLoading || !user) return;
    const ref = readTmRef();
    if (!ref) return;
    if (attachedRef.current === `${user.user_id}:${ref}`) return;
    attachedRef.current = `${user.user_id}:${ref}`;
    api.post("/session/affiliate", { ref }).catch(() => {});
  }, [user, authLoading]);

  // Drain the "pending add to cart" once the user finishes signing in. This
  // resumes the exact click a guest made on the product page before they were
  // bounced to Google OAuth, then lands them on /cart with the item already in.
  const drainedPendingAdd = useRef(false);
  useEffect(() => {
    if (authLoading || !user || drainedPendingAdd.current) return;
    let pending;
    try { pending = JSON.parse(localStorage.getItem(PENDING_ADD_KEY) || "null"); } catch { pending = null; }
    if (!pending || !pending.product_id) return;
    // Ignore entries older than 30 minutes — likely stale from a different session.
    if (pending.at && Date.now() - pending.at > 30 * 60 * 1000) {
      localStorage.removeItem(PENDING_ADD_KEY);
      return;
    }
    drainedPendingAdd.current = true;
    (async () => {
      try {
        const { data } = await api.post("/cart", {
          product_id: pending.product_id,
          quantity: pending.quantity || 1,
          size: pending.size || null,
          color: pending.color || null,
        });
        setItems(data);
        toast.success("Signed in — item added to your cart");
        navigate("/cart", { replace: true });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Could not add the item to your cart");
      } finally {
        localStorage.removeItem(PENDING_ADD_KEY);
      }
    })();
  }, [user, authLoading, navigate]);

  // Auto-revalidate every applied code whenever cart contents change OR on initial load
  // (covers page refresh and direct navigation to /checkout). Gated behind cartLoaded
  // so we don't wipe rehydrated discounts before /cart returns.
  useEffect(() => {
    if (!cartLoaded) return;
    if (discounts.length === 0) return;
    if (items.length === 0) { setDiscounts([]); return; }
    let cancelled = false;
    (async () => {
      const next = [];
      const dropped = [];
      for (const d of discounts) {
        try {
          const { data } = await api.post("/discounts/apply", { code: d.code });
          next.push(data);
        } catch {
          dropped.push(d.code);
        }
      }
      if (cancelled) return;
      const changed =
        next.length !== discounts.length ||
        next.some((d, i) => d.discount_amount !== discounts[i]?.discount_amount);
      if (changed) setDiscounts(next);
      if (dropped.length) {
        toast.error(`Removed expired/invalid code${dropped.length > 1 ? "s" : ""}: ${dropped.join(", ")}`);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, cartLoaded]);

  // Promo link auto-apply: tries to apply localStorage `marq_pending_promo`
  // whenever the cart has items and the user is authenticated. Stays pending
  // (does NOT toast errors) until the buyer adds a matching seller's product;
  // surfaces a single success toast when finally applied.
  useEffect(() => {
    if (!cartLoaded || !user) return;
    if (items.length === 0) return;
    let pending = null;
    try { pending = localStorage.getItem(PENDING_PROMO_KEY); } catch { /* ignore */ }
    if (!pending) return;
    if (discounts.some((d) => d.code === pending)) {
      try { localStorage.removeItem(PENDING_PROMO_KEY); } catch { /* ignore */ }
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.post("/discounts/apply", { code: pending });
        if (cancelled) return;
        if (discounts.some((d) => d.seller_id === data.seller_id)) {
          // a code from this seller is already applied — drop the promo silently
          try { localStorage.removeItem(PENDING_PROMO_KEY); } catch { /* ignore */ }
          return;
        }
        setDiscounts((prev) => [...prev, data]);
        toast.success(`Promo applied: ${data.code} — ${data.message || ""}`);
        try { localStorage.removeItem(PENDING_PROMO_KEY); } catch { /* ignore */ }
      } catch {
        // leave pending; will retry on next items/discounts change
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, cartLoaded, user, discounts.length]);

  const add = async ({ product_id, quantity = 1, size = null, color = null }) => {
    if (!user) {
      // Save the intended add so we can complete it after Google sign-in returns.
      // The pending-add is drained by the effect below once the user session lands.
      try {
        localStorage.setItem(PENDING_ADD_KEY, JSON.stringify({
          product_id, quantity, size, color, at: Date.now(),
        }));
      } catch { /* quota; fall through — sign-in will still work but item won't auto-add */ }
      toast("Sign in to continue", {
        description: "We'll drop this item into your cart once you're signed in.",
      });
      loginWithGoogle();
      return false;
    }
    try {
      const { data } = await api.post("/cart", { product_id, quantity, size, color });
      setItems(data);
      toast.success("Added to cart");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add to cart");
      return false;
    }
  };

  const updateLine = async (line_id, quantity) => {
    const { data } = await api.put(`/cart/line/${encodeURIComponent(line_id)}`, { quantity });
    setItems(data);
  };

  const removeLine = async (line_id) => {
    const { data } = await api.delete(`/cart/line/${encodeURIComponent(line_id)}`);
    setItems(data);
  };

  const applyCode = async (rawCode) => {
    const code = (rawCode || "").trim().toUpperCase();
    if (!code) {
      toast.error("Enter a code");
      return false;
    }
    if (discounts.some((d) => d.code === code)) {
      toast.error("Code already applied");
      return false;
    }
    try {
      const { data } = await api.post("/discounts/apply", { code });
      if (discounts.some((d) => d.seller_id === data.seller_id)) {
        toast.error(`A code is already applied for ${data.seller_name}`);
        return false;
      }
      setDiscounts((prev) => [...prev, data]);
      toast.success(data.message || "Code applied");
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid code");
      return false;
    }
  };

  const removeCode = (code) => {
    setDiscounts((prev) => prev.filter((d) => d.code !== code));
  };

  const clearDiscounts = () => setDiscounts([]);

  const getAffiliateRef = () => readTmRef();
  const clearAffiliateRef = () => clearTmRef();

  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const discountTotal = discounts.reduce((s, d) => s + (d.discount_amount || 0), 0);

  return (
    <CartContext.Provider
      value={{
        items, add, updateLine, removeLine, refresh, count, subtotal,
        discounts, applyCode, removeCode, clearDiscounts, discountTotal,
        getAffiliateRef, clearAffiliateRef,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
